import { useCallback, useState } from 'react';
import {
  useDeactivateMemberMutation,
  useReactivateMemberMutation
} from '@bo-services/members/membersApi';
import type { ComboboxOption } from '@library/components/selects';

export interface MemberActivationProps {
  isDeactivated: boolean;
  memberId: string;
}

export const useMemberActivation = ({
  isDeactivated,
  memberId
}: MemberActivationProps) => {
  const [deactivationReason, setDeactivationReason] =
    useState<ComboboxOption | null>(null);

  const [
    deactivateMember,
    {
      isLoading: isDeactivateLoading,
      isError: isDeactivateError,
      isSuccess: isDeactivateSuccess
    }
  ] = useDeactivateMemberMutation();

  const [
    reactivateMember,
    {
      isLoading: isReactivateLoading,
      isError: isReactivateError,
      isSuccess: isReactivateSuccess
    }
  ] = useReactivateMemberMutation();

  const handleSubmit = useCallback(async () => {
    const deactivateReason = deactivationReason?.value.toString();
    const activeMember = !isDeactivated && !!deactivateReason;

    if (activeMember) {
      await deactivateMember({
        member_id: memberId,
        deactivation_reason: deactivateReason
      }).unwrap();
      return;
    }

    if (!activeMember) {
      await reactivateMember({ member_id: memberId }).unwrap();
    }
  }, [
    isDeactivated,
    deactivationReason,
    deactivateMember,
    memberId,
    reactivateMember
  ]);

  const isActivationError = isDeactivateError || isReactivateError;
  const isActivationLoading = isDeactivateLoading || isReactivateLoading;
  const isActivationSuccess = isDeactivateSuccess || isReactivateSuccess;

  return {
    deactivationReason,
    isActivationError,
    isActivationLoading,
    isActivationSuccess,
    onDeactivationReasonChange: setDeactivationReason,
    onSubmit: handleSubmit
  };
};
